import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { error, neutral } from "app/themes/colors";
import { appointmentFormMetadata } from "components/custom-print-form-preview/appointment-form-metadata.util";
import CustomPrintFormPreviewDialog from "components/custom-print-form-preview/custom-print-form-preview-dialog.component";
import SurfaceDigitalFormButton, {
  type SurfaceDigitalFormHandle
} from "components/custom-print-form-preview/surface-digital-form-button.component";
import { useAppointmentFormContext } from "components/custom-print-form-preview/use-appointment-form-context.hook";
import useCustomPrintFormPreview from "components/custom-print-form-preview/use-custom-print-form-preview.hook";
import FormTemplatesField from "components/form-templates-field";
import { isDigitalForm } from "components/form-templates-field/digital-forms.utils";
import usePreviewPdfForm from "components/preview-pdf-form-dialog/use-preview-pdf-form.hook";
import {
  DIGITAL_FORM_TAGS_APPOINTMENT_FORMS,
  HTML_FORMS_FEATURE,
  SIGN_AND_UPLOAD_APPOINTMENT_FORMS,
  WORD_PROCESSOR_FORMS
} from "constants/feature-flags";
import {
  APPOINTMENT_FORM_TEMPLATE_TYPE,
  CUSTOM_FORM_TEMPLATE_TYPE
} from "constants/form-templates";
import type { Appointment } from "services/types/appointments.type";
import type { FormTemplate } from "services/types/form-templates.type";

import {
  type PatientFormsValues,
  resolveAppointmentId,
  useFormTemplates
} from "./use-form-templates.hook";

type AppointmentFormsDownloadProps = {
  disablePortal?: boolean;
};

const DIGITAL_FORM_CAPTION =
  "Digital form: opens with this appointment's details filled in. Edit, sign, print or save to E-Docs.";
const DIGITAL_FORM_UNAVAILABLE =
  "We couldn't load this appointment's details, so this form can't open. Please try again.";

type DigitalFormButtonProps = {
  selected: FormTemplate;
  appointment: Appointment;
  patientId?: number;
  onUnavailable: () => void;
};

/**
 * ENG-2499: mounted only while a Digital Form is picked, so with the flags off (or an Anvil row
 * picked) none of its reads run. A form that can't read its appointment can't open, so it never
 * prints blank by accident.
 */
function DigitalFormButton({
  selected,
  appointment,
  patientId,
  onUnavailable
}: DigitalFormButtonProps) {
  const digitalFormRef = useRef<SurfaceDigitalFormHandle>(null);
  const {
    boundAppointment,
    additionalMergeTagsValues,
    isLoading,
    isUnavailable
  } = useAppointmentFormContext({
    appointmentId: resolveAppointmentId(appointment),
    enabled: true
  });
  const metadata = useMemo(
    () =>
      boundAppointment && {
        ...appointmentFormMetadata(boundAppointment),
        appointmentId: boundAppointment.id
      },
    [boundAppointment]
  );

  if (isUnavailable) {
    return (
      <Typography variant="caption" color={error[700]}>
        {DIGITAL_FORM_UNAVAILABLE}
      </Typography>
    );
  }

  return (
    <SurfaceDigitalFormButton
      ref={digitalFormRef}
      placement={APPOINTMENT_FORM_TEMPLATE_TYPE}
      patientId={patientId}
      selected={selected}
      boundAppointment={boundAppointment}
      additionalMergeTagsValues={additionalMergeTagsValues}
      metadata={metadata}
      size="small"
      disabled={isLoading || !boundAppointment}
      onClick={() => digitalFormRef.current?.open(onUnavailable)}
      sx={{ whiteSpace: "nowrap" }}
    />
  );
}

/** ENG-2499: what a Digital Form changes on this surface. Nothing here applies unless both flags are on. */
const useAppointmentDigitalForm = (
  formTemplate: FormTemplate | null | undefined,
  patientId: number | undefined,
  dialogOpened: boolean
) => {
  const flags = useFlags();
  const enabled = Boolean(
    flags[HTML_FORMS_FEATURE] && flags[DIGITAL_FORM_TAGS_APPOINTMENT_FORMS]
  );
  return {
    digitalFormsEnabled: enabled,
    digitalForm:
      enabled && formTemplate && isDigitalForm(formTemplate)
        ? formTemplate
        : undefined,
    // A Digital Form's dialog holds what opening it set, and the surface's reset would clear that if the appointment
    // is re-read underneath it. The reset runs when the dialog closes instead.
    isDialogHeld: enabled && dialogOpened,
    digitalFormsPlacement:
      enabled && patientId ? APPOINTMENT_FORM_TEMPLATE_TYPE : undefined
  };
};

export default function AppointmentFormsDownload({
  disablePortal = true
}: AppointmentFormsDownloadProps) {
  const flags = useFlags();
  const wordProcessorFormsEnabled = flags[WORD_PROCESSOR_FORMS];
  const isSignAndUploadEnabled = flags[SIGN_AND_UPLOAD_APPOINTMENT_FORMS];

  const {
    showDialog,
    setPatientId,
    handlePrint,
    formContent,
    isReady,
    setTemplate,
    setMetadata,
    setFormContent,
    dialogOpened
  } = useCustomPrintFormPreview();

  const {
    control,
    onSubmit,
    isError,
    isLoading,
    appointment,
    patientId,
    onCreateForm,
    handleSubmitSignature,
    clearValues
  } = useFormTemplates();

  const values = useWatch({ control });
  const formTemplate = values.form_template as FormTemplate | null | undefined;
  const {
    digitalFormsEnabled,
    digitalForm,
    isDialogHeld,
    digitalFormsPlacement
  } = useAppointmentDigitalForm(formTemplate, patientId, dialogOpened);

  useEffect(() => {
    if (isDialogHeld) return;
    if (
      wordProcessorFormsEnabled &&
      formTemplate?.type === CUSTOM_FORM_TEMPLATE_TYPE
    ) {
      setPatientId(patientId);
      setMetadata(appointmentFormMetadata(appointment));
      setTemplate(formTemplate);
    } else {
      setPatientId(undefined);
      setMetadata({});
      setTemplate(undefined);
      // Or Create my Custom Form… would open holding the last Digital Form's filled-in values.
      if (digitalFormsEnabled) setFormContent("");
    }
  }, [
    digitalFormsEnabled,
    isDialogHeld,
    setFormContent,
    wordProcessorFormsEnabled,
    patientId,
    setPatientId,
    appointment,
    formTemplate,
    setTemplate,
    setMetadata
  ]);

  const isFormTemplate =
    formTemplate?.type === CUSTOM_FORM_TEMPLATE_TYPE &&
    wordProcessorFormsEnabled;

  useEffect(() => {
    if (isFormTemplate) {
      showDialog();
    }
  }, [isFormTemplate, showDialog]);

  const { loadPdfContent, handleShowPreviewModal } = usePreviewPdfForm();

  const isPrintDisabled =
    !formTemplate || isLoading || (isFormTemplate && !isReady);

  const generatePdfFnCallback = useCallback(
    async (data?: Record<string, any>) => {
      const { id, file } = await onCreateForm(values as PatientFormsValues);
      if (id) {
        const signatureFiles = data?.signature ? [data.signature] : [];
        return await handleSubmitSignature(signatureFiles, id);
      }
      return file;
    },
    [handleSubmitSignature, onCreateForm, values]
  );

  const handleShowPdfPreview = useCallback(async () => {
    const { id, file } = await onCreateForm(values as PatientFormsValues);
    const blob = id ? await handleSubmitSignature([], id) : file;
    loadPdfContent({
      blob: blob ?? file,
      generatePdfFn: generatePdfFnCallback
    });
    handleShowPreviewModal(`Preview ${formTemplate?.title}`);
  }, [
    formTemplate?.title,
    generatePdfFnCallback,
    handleSubmitSignature,
    handleShowPreviewModal,
    loadPdfContent,
    onCreateForm,
    values
  ]);

  const handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      // A Digital Form opens from its own button; Enter in the picker must not send it to Anvil.
      if (digitalForm) {
        e.preventDefault();
      } else if (isFormTemplate) {
        e.preventDefault();
        handlePrint(formContent);
      } else {
        onSubmit(e);
      }
    },
    [digitalForm, formContent, handlePrint, isFormTemplate, onSubmit]
  );

  return (
    <>
      <Grid
        container
        component="form"
        onClick={(e: SyntheticEvent) => e.stopPropagation()}
        onSubmit={handleSubmit}
        direction="row"
        alignItems="center"
        spacing={1}
      >
        <Grid item xs={8} pb={1}>
          <FormTemplatesField
            control={control}
            name="form_template"
            label="Appointment Form"
            placeholder="Select form to Print"
            type={APPOINTMENT_FORM_TEMPLATE_TYPE}
            createYourOwnFormEnabled={wordProcessorFormsEnabled}
            noLabel
            beforeSelect={clearValues}
            disablePortal={disablePortal}
            sx={{ ml: 0 }}
            digitalFormsPlacement={digitalFormsPlacement}
          />
        </Grid>
        <Grid item xs={4}>
          <Stack direction="row" columnGap={1} pl={1}>
            {/* Both icon buttons in this row had no accessible name and no tooltip, so a
                screen reader announced them as "button" and a sighted user had to click
                one to find out what it did. The Sign button below already carries a
                Tooltip; this matches it rather than introducing a second pattern. The
                <span> is required because a disabled MUI button fires no hover events, so
                the tooltip would never open — the same wrapper the Sign button uses when
                it is disabled. */}
            {digitalForm ? (
              <DigitalFormButton
                selected={digitalForm}
                appointment={appointment}
                patientId={patientId}
                onUnavailable={clearValues}
              />
            ) : (
              <Tooltip title="Print the selected form">
                <span>
                  <IconButton
                    type="submit"
                    aria-label="Print the selected form"
                    disabled={isPrintDisabled}
                    size="small"
                  >
                    {isLoading ? <CircularProgress size={24} /> : <PrintIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {isSignAndUploadEnabled &&
              !digitalForm &&
              formTemplate?.type !== CUSTOM_FORM_TEMPLATE_TYPE && (
                <Box>
                  <Tooltip title="Sign Form and Upload to E-Docs">
                    {isPrintDisabled ? (
                      <span>
                        {/* The `aria-label` is on the BUTTON, not left to the Tooltip.
                            MUI copies a tooltip's title onto its child as an aria-label,
                            but the child here is the <span> wrapper the disabled state
                            needs — so the button itself was reaching assistive tech with
                            no name at all, exactly like the two below it. Measured in a
                            browser on /scheduling, 2026-09-17. */}
                        <IconButton
                          size="small"
                          aria-label="Sign form and upload to E-Docs"
                          onClick={handleShowPdfPreview}
                          disabled={isPrintDisabled}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <EditIcon
                              style={{
                                height: "24px",
                                width: "24px",
                                fill: neutral[400]
                              }}
                            />
                          )}
                        </IconButton>
                      </span>
                    ) : (
                      <IconButton
                        size="small"
                        aria-label="Sign form and upload to E-Docs"
                        onClick={handleShowPdfPreview}
                        disabled={isPrintDisabled}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <EditIcon
                            style={{
                              height: "24px",
                              width: "24px",
                              fill: neutral[800]
                            }}
                          />
                        )}
                      </IconButton>
                    )}
                  </Tooltip>
                </Box>
              )}

            {isFormTemplate && (
              <Tooltip title="Open this form to edit before printing">
                <IconButton
                  size="small"
                  aria-label="Open this form to edit before printing"
                  onClick={showDialog}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>
        {digitalForm && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {DIGITAL_FORM_CAPTION}
            </Typography>
          </Grid>
        )}
        {isError && (
          <Grid item xs={12}>
            <Typography variant="body1" fontWeight={700} color={error[700]}>
              An Error has Occurred, please reach out to support.
            </Typography>
          </Grid>
        )}
      </Grid>
      <CustomPrintFormPreviewDialog />
    </>
  );
}
